import * as yup from 'yup';

import {
    Box,
    Button,
    TextField,
    Typography
} from '@mui/material';

import LoadingButton from '@mui/lab/LoadingButton';
import PhoneInputTextField from '../PhoneInputTextField';
import PhoneNumberInput from "react-phone-number-input/input";
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

const contactFormSchema = yup.object({
    name: yup.string().required(),
    email: yup.string().email(),
    phoneNumber: yup.string().test("consultantPhoneTest", "Must be a valid phone number", (value) => {
        if (!value) {
            return true;
        }
        return isPossiblePhoneNumber(`${value}` || "", { defaultCountry: "CA" });
    })
});

export type ContactFormData = yup.InferType<typeof contactFormSchema>;

type ContactFormProps = {
    onCancel: () => void;
    onSubmit: (data: ContactFormData) => (void | Promise<void>);
    existingContact?: ContactFormData;
};

export const ContactForm = ({ onCancel, onSubmit, existingContact }: ContactFormProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const snackbar = useSnackbar();
    const formik = useFormik<ContactFormData>({
        initialValues: existingContact || {
            name: "",
            email: "",
            phoneNumber: ""
        },
        validationSchema: contactFormSchema,
        onSubmit: async (values) => {
            try {
                setIsSubmitting(true);
                await onSubmit(values);
            } catch (e) {
                snackbar.enqueueSnackbar("Error occurred while creating contact", { variant: "error" });
            } finally {
                setIsSubmitting(false);
            }
        }
    });

    return (
        <Box
            component="form"
            onSubmit={formik.handleSubmit}
            display="grid"
            gridTemplateColumns="max-content auto"
            alignItems="center"
            justifyItems="center"
            gap={1}
        >
            <Typography>Name</Typography>
            <TextField
                fullWidth
                size="small"
                id="name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
            />
            <Typography>Email</Typography>
            <TextField
                fullWidth
                size="small"
                id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
            />
            <Typography>Phone</Typography>
            <PhoneNumberInput
                defaultCountry="CA"
                fullWidth
                inputComponent={PhoneInputTextField as any}
                id="phoneNumber"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={(phoneNumber) => {
                    formik.setFieldValue("phoneNumber", phoneNumber ?? "");
                }}
                error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                variant="outlined"
                size="small"
            />
            <Box display="flex" justifyContent="flex-end" gap={2} gridColumn="1/-1" width="100%">
                <Button
                    variant="outlined"
                    onClick={() => onCancel()}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    loading={isSubmitting}
                >
                    Submit
                </LoadingButton>
            </Box>
        </Box>
    );
};